/**
 * Canonical site constants and the schema.org builders shared across layouts.
 *
 * BASE_URL was previously redeclared in sitemap.ts, robots.ts and the product
 * layout. Structured data has to agree with itself across every page — an
 * Organization `@id` that differs by one character from the one a Product's
 * publisher points at silently breaks the association — so the origin lives
 * here and nowhere else.
 */

import type { Metadata } from 'next';

export const BASE_URL = 'https://supa.place';
export const SITE_NAME = 'supa.place';

/**
 * The picture a pasted link shows (og:image and the X card) on any page without
 * one of its own: the wordmark on white, 1200x630. White because chat apps draw
 * the card on their own background, where black letters on a transparent image
 * vanish in dark mode. Platforms cache a preview by image URL, so bump `v`
 * whenever the picture changes.
 */
export const SHARE_IMAGE = { url: '/og-image.png?v=2', width: 1200, height: 630, alt: SITE_NAME };

/**
 * Spread FIRST into every `openGraph` and `twitter` a page sets. Next merges
 * metadata one level deep: a page's `openGraph` replaces the root layout's
 * whole, so a page that sets only its title, description and url ships no
 * image, no site name and no locale, and its X card falls back to "summary".
 * A product cover or storefront avatar overrides the image after the spread.
 * share-metadata.test.ts fails any page that skips these.
 */
export const SHARE_OPEN_GRAPH = {
  siteName: SITE_NAME,
  locale: 'en_AU',
  type: 'website',
  images: [SHARE_IMAGE],
} satisfies NonNullable<Metadata['openGraph']>;

export const SHARE_TWITTER = {
  card: 'summary_large_image',
  images: [SHARE_IMAGE.url],
} satisfies NonNullable<Metadata['twitter']>;

/**
 * Stable @id anchors. Search engines dedupe entities by @id, so these strings
 * must never change once they've been crawled.
 */
export const ORGANIZATION_ID = `${BASE_URL}/#organization`;
export const WEBSITE_ID = `${BASE_URL}/#website`;

export const CONTACT_EMAIL = 'support@supa.place';
export const CONTACT_PHONE = '+61481069946';
/** Registered business name + ABN, matching the footer's business identification. */
export const LEGAL_NAME = 'BIESSE, NOAH HARVARD';
export const ABN = '44590119237';

export type JsonLd = Record<string, unknown>;

/**
 * The publisher entity. Referenced by @id from the WebSite node and from every
 * Product page's breadcrumb trail, which is what lets a search engine treat the
 * whole domain as one brand rather than a pile of unrelated pages.
 *
 * Deliberately omits `address`. The trading address is a real residential one;
 * it is disclosed in the footer because distance-selling law requires it, but
 * emitting it as machine-readable structured data actively feeds it into
 * knowledge graphs and scrapers, which is a different exposure. Add a
 * PostalAddress here only once that address is a commercial one.
 */
export function buildOrganizationJsonLd(): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: SITE_NAME,
    legalName: LEGAL_NAME,
    url: BASE_URL,
    logo: {
      '@type': 'ImageObject',
      url: `${BASE_URL}/logo.png`,
    },
    image: `${BASE_URL}${SHARE_IMAGE.url}`,
    description:
      'A marketplace for digital products from independent creators; design assets, code, templates, courses, ebooks, audio and fonts.',
    email: CONTACT_EMAIL,
    telephone: CONTACT_PHONE,
    // Australian Business Number, the identifier a knowledge panel can verify
    // this entity against.
    identifier: {
      '@type': 'PropertyValue',
      name: 'ABN',
      value: ABN,
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: CONTACT_EMAIL,
      telephone: CONTACT_PHONE,
      availableLanguage: ['en'],
    },
  };
}

/**
 * The site entity, plus the SearchAction that makes a sitelinks searchbox
 * possible. `/search?q=` is the real search route — see src/app/search/page.tsx,
 * which reads exactly this parameter.
 */
export function buildWebSiteJsonLd(): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    name: SITE_NAME,
    url: BASE_URL,
    publisher: { '@id': ORGANIZATION_ID },
    inLanguage: 'en',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${BASE_URL}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

export type Crumb = { name: string; path: string };

/**
 * Build a BreadcrumbList from the site root down to the current page.
 *
 * Pass only the trail BELOW the homepage; the root crumb is prepended here so
 * every page's first position is identical. `path` is origin-relative and gets
 * resolved against BASE_URL, because Google requires absolute URLs in `item`.
 */
export function buildBreadcrumbJsonLd(trail: Crumb[]): JsonLd {
  const crumbs: Crumb[] = [{ name: 'Home', path: '/' }, ...trail];
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: `${BASE_URL}${crumb.path}`,
    })),
  };
}
